"use strict";

exports.PresendVerificationSDK = void 0;
var _ethers = require("ethers");
var _logger = require("./logger");
var _utils = require("./utils");
var _sdkTypes = require("./data/sdk-types");
var _constants = require("./data/constants");
var _VerificationHttpRepository = require("./repositories/VerificationHttpRepository");
var _ReferralHttpRepository = require("./repositories/ReferralHttpRepository");
var _presendPaymentAbi = require("./data/abis/presendPaymentAbi");
var _DexScreenerHttpRepository = require("./repositories/DexScreenerHttpRepository");
var _VerificationFailedError = require("./data/errors/VerificationFailedError");
var _erc721Abi = require("./data/abis/erc721Abi");
var _ProtocolError = require("./data/errors/ProtocolError");
var _ReferralError = require("./data/errors/ReferralError");
var _DexScreenerError = require("./data/errors/DexScreenerError");
var _TransactionHttpRepository = require("./repositories/TransactionHttpRepository");
class PresendVerificationSDK {
  /**
   * Creates an instance of the `PresendVerificationSDK` class.
   *
   * @param {PresendVerificationSDKProps} props - The properties for creating an instance of the `PresendVerificationSDK` class.
   * @param {ethers.providers.Provider} props.provider - An `ethers` provider for making transactions.
   * @param {Signer} props.signer - An `ethers` signer for signing messages.
   */
  constructor(props) {
    var _props$debug;
    if (!props) {
      throw new _ProtocolError.ProtocolError('props not provided', {
        component: _sdkTypes.ErrorContextComponent.SDK_PRESEND_VERIFICATION,
        description: 'Props required for SDK initialization were not provided'
      });
    }
    if (props.signer) this.signer = props.signer;
    this.logger = new _logger.Logger('@presend/verification/sdk', (_props$debug = props.debug) !== null && _props$debug !== void 0 ? _props$debug : false);
    this.verificationHttpRepo = new _VerificationHttpRepository.VerificationHttpRepository(_constants.VerificationURI.US);
    this.transactionHttpRepo = new _TransactionHttpRepository.TransactionHttpRepository();
    this.referralHttpRepo = new _ReferralHttpRepository.ReferralHttpRepository();
    this.dexScreenerHttpRepo = new _DexScreenerHttpRepository.DexScreenerHttpRepository();
  }

  /**
   * Verify a transaction by obtaining an approved payment token and using it to authorize the transaction.
   * If a payment is required for the transaction, it will be processed before the verification.
   * If an existing payment token is provided, it will be used instead of generating a new one.
   * If an `existingToken` is provided, you must set the `skipPayment` flag to `true`.
   *
   * @param {TransactionVerificationProps} props - The properties for verifying a transaction.
   * @param {VerificationPayload} props.payload - The payload for the transaction to be verified.
   * @param {boolean} [props.skipPayment] - A flag indicating whether to skip the payment process.
   * @param {string} [props.existingToken] - An existing payment token to be used for the transaction.
   * @param {string} [props.transactionId] - An existing transaction Id used to resume a transaction.
   * @return {Promise<VerificationResponse>} A promise that resolves with the result of the transaction verification.
   */
  async verifyTransactionAsync(props) {
    this.logger.debug('Verifying transaction payload', props);
    try {
      var _this$signer, _payload$targetTokenA;
      if (!props) {
        throw new _ProtocolError.ProtocolError('props not provided', {
          component: _sdkTypes.ErrorContextComponent.SDK_VERIFY_TRANSACTION,
          description: 'Props required to verify transaction were not provided'
        });
      }
      const {
        payload,
        skipPayment,
        existingToken,
        signer
      } = props;
      if (payload.targetTokenAmount <= 0) {
        throw new _ProtocolError.ProtocolError("targetTokenAmount cannot be 0 or negative", {
          component: _sdkTypes.ErrorContextComponent.SDK_VERIFY_TRANSACTION,
          description: "Target amount must a positive number"
        });
      }
      if (signer) this.signer = signer;
      const signerAddress = await ((_this$signer = this.signer) === null || _this$signer === void 0 ? void 0 : _this$signer.getAddress());
      if (!signerAddress) {
        throw new _ProtocolError.ProtocolError('Signer address not provided', {
          component: _sdkTypes.ErrorContextComponent.SDK_VERIFY_TRANSACTION,
          description: 'Signer address required to verify transaction was not provided'
        });
      }
      if (signerAddress.toLowerCase() !== payload.sourceAddress.toLowerCase()) {
        throw new _ProtocolError.ProtocolError('Invalid signer address', {
          component: _sdkTypes.ErrorContextComponent.SDK_VERIFY_TRANSACTION,
          description: 'The signer address does not match the source address'
        });
      }
      if (props.payload.targetCryptoApi === 'binancedotcom') {
        this.logger.debug("Binancedotcom detected, adjusting for Canadian stack @ -> ".concat(_constants.VerificationURI.CA));
        this.verificationHttpRepo = new _VerificationHttpRepository.VerificationHttpRepository(_constants.VerificationURI.CA);
      }

      // "existingToken" is used when the user has already acquired a token after paying the fee
      // "skipPayment" is used when the user has already paid the fee and we don't want to process it again
      // "existingToken" must be used with "skipPayment" because new payment would not be reflected within the provided `existingToken`
      if (existingToken && skipPayment === false) {
        throw new _ProtocolError.ProtocolError('Invalid combination of flags', {
          component: _sdkTypes.ErrorContextComponent.SDK_VERIFY_TRANSACTION,
          description: 'If an `existingToken` is provided, you must set the `skipPayment` flag to `true`'
        });
      }
      let token;
      let metadata = {};
      let transactionId;

      // payment smart contract was never designed to accept native tokens as a way to transact
      // so we use a placeholder address in order to reference payments made with the native token
      const targetTokenAddressFormattedForPayment = payload.targetTokenAddress === 'native' ? _constants.NATIVE_TOKEN_PLACEHOLDER_ADDRESS : payload.targetTokenAddress.valueOf();
      const networks = await this.verificationHttpRepo.getSupportedNetworksAsync();
      let supportedNetwork;

      // default back to targetNetwork
      if (payload.targetNetwork) {
        supportedNetwork = networks.find(network => {
          var _payload$targetNetwor;
          return network.networkSlug === ((_payload$targetNetwor = payload.targetNetwork) === null || _payload$targetNetwor === void 0 ? void 0 : _payload$targetNetwor.toLowerCase());
        });
      }

      // prefer targetChainIdHex
      if (payload.targetChainIdHex) {
        supportedNetwork = networks.find(network => {
          var _payload$targetChainI;
          return network.chainId === ((_payload$targetChainI = payload.targetChainIdHex) === null || _payload$targetChainI === void 0 ? void 0 : _payload$targetChainI.toLowerCase());
        });
      }
      if (!supportedNetwork) {
        throw new _ProtocolError.ProtocolError("Target Network ChainID ".concat(payload.targetChainIdHex, " not supported"), {
          component: _sdkTypes.ErrorContextComponent.SDK_VERIFY_TRANSACTION,
          description: 'The target network chain ID Hex is not supported by presend at this time'
        });
      }
      const targetNetwork = supportedNetwork.networkSlug;

      // source address eligible for bypass if they own a presend NFT
      const bscNetwork = networks.find(network => network.networkSlug === 'bsc');
      if (!bscNetwork) {
        throw new _ProtocolError.ProtocolError('BSC network not found', {
          component: _sdkTypes.ErrorContextComponent.SDK_VERIFY_TRANSACTION,
          description: 'The Binance Smart Chain network was not found and is required for verification. This is most likely due to a misconfiguration'
        });
      }
      const tokenAmountInDecimals = _ethers.ethers.utils.parseUnits(payload.targetTokenAmount.toString(), (_payload$targetTokenA = payload.targetTokenAmountDecimals) !== null && _payload$targetTokenA !== void 0 ? _payload$targetTokenA : 18);
      const targetTokenAmountHex = tokenAmountInDecimals.toHexString();
      const signedSourceAddressValidation = {
        targetNetwork,
        targetTokenAddress: targetTokenAddressFormattedForPayment,
        sourceAddress: payload.sourceAddress,
        targetAddress: payload.targetAddress,
        targetTokenAmountHex,
        targetTokenSymbol: payload.targetTokenSymbol,
        targetCryptoApi: payload.targetCryptoApi
      };
      if (!this.signer) {
        throw new _ProtocolError.ProtocolError('signer not provided', {
          component: _sdkTypes.ErrorContextComponent.SDK_GET_APPROVED_PAYMENT_TOKEN,
          description: 'Signer Prop required for getting approved payment token was not provided'
        });
      }
      const signedPayload = await this.signer.signMessage(JSON.stringify(signedSourceAddressValidation));
      if (!props.transactionId) {
        transactionId = await this.transactionHttpRepo.insertBaseTransaction({
          signedPayload,
          payload: signedSourceAddressValidation
        });
      } else {
        transactionId = props.transactionId;
      }
      const sourceEligibleForBypass = await this.getBypassEligibilityAsync(payload.sourceAddress, bscNetwork);
      this.logger.debug('Source address eligible for bypass', sourceEligibleForBypass);
      if (sourceEligibleForBypass === false && !skipPayment) {
        metadata = await this.processPaymentAsync({
          transactionId,
          payload,
          supportedNetwork,
          targetTokenAddress: targetTokenAddressFormattedForPayment
        });
      }
      if (existingToken) {
        if (!(0, _utils.isTokenValid)(existingToken)) {
          throw new _ProtocolError.ProtocolError('Existing Token is not valid', {
            component: _sdkTypes.ErrorContextComponent.SDK_VERIFY_TRANSACTION,
            description: 'Existing token prop is either expired or invalid'
          });
        }
        token = existingToken;
      } else {
        token = await this.getApprovedPaymentTokenAsync(signedSourceAddressValidation, signedPayload, transactionId);
      }
      const verificationResponse = await this.verificationHttpRepo.verifyTransactionAsync(payload, metadata, token, transactionId, targetNetwork, targetTokenAmountHex);
      this.logger.debug('Successfully verified transaction payload');
      return verificationResponse;
    } catch (error) {
      // only log if it's an unexpected error
      if (!(error instanceof _VerificationFailedError.VerificationFailedError)) this.logger.error("".concat(error.name, ": Unexpected Error while trying to verify transaction :: ").concat(error.message), error);
      throw error;
    }
  }

  /**
   * Resumes a partially processed transaction based on transaction status
   * @param {string} transactionId - The transaction id of the transaction you would like to resume.
   * @return {Promise<VerificationResponse>} A promise that resolves with the result of the transaction verification.
   */
  async resumeTransactionVerificationAsync(transactionId) {
    try {
      this.logger.debug('Getting transaction by id', transactionId);
      const transaction = await this.transactionHttpRepo.getTransactionByIdAsync(transactionId);
      if (!transaction) {
        throw new _ProtocolError.ProtocolError('Unable to find transaction with id', {
          component: _sdkTypes.ErrorContextComponent.SDK_RESUME_TRANSACTION,
          description: "Transaction with ID ".concat(transactionId, " was not found or invalid")
        });
      }
      if (!(transaction.status in _sdkTypes.TransactionStatus)) {
        throw new _ProtocolError.ProtocolError('Invalid transaction status', {
          component: _sdkTypes.ErrorContextComponent.SDK_RESUME_TRANSACTION,
          description: "Transaction with ID ".concat(transactionId, " is not in a valid state")
        });
      }
      if (transaction.status === _sdkTypes.TransactionStatus.Verified) {
        throw new _ProtocolError.ProtocolError('Transaction has already been verified!', {
          component: _sdkTypes.ErrorContextComponent.SDK_RESUME_TRANSACTION,
          description: "Transaction with ID ".concat(transactionId, " is already in a \"verified\" state")
        });
      }
      this.logger.debug('Successfully got transaction', transaction);
      const verificationPayload = {
        sourceAddress: transaction.sourceAddress,
        targetAddress: transaction.targetAddress,
        targetCryptoApi: transaction.targetCryptoApi,
        targetTokenAddress: transaction.targetTokenAddress,
        targetTokenSymbol: transaction.targetTokenSymbol,
        targetTokenAmount: Number(_ethers.ethers.utils.formatUnits(_ethers.BigNumber.from(transaction.targetTokenAmount), 'ether')),
        targetNetwork: transaction.targetNetwork
      };
      this.logger.debug('verifying transaction with payload', verificationPayload);
      const verificationResponse = await this.verifyTransactionAsync({
        payload: verificationPayload,
        skipPayment: transaction.status === _sdkTypes.TransactionStatus.Paid,
        transactionId
      });
      this.logger.debug('Successfully resumed transaction payload', verificationResponse);
      return verificationResponse;
    } catch (error) {
      if (!(error instanceof _ProtocolError.ProtocolError)) this.logger.error("".concat(error.name, ": Unexpected Error while trying to resume transaction :: ").concat(error.message), error);
      throw error;
    }
  }

  /**
   * Get an approved payment token.
   * Used to verify that a user has paid the Presend fee to a specific address.
   * @param {ApprovedPaymentTokenProps} props - The properties for generating an approved payment token.
   * @return {Promise<string>} A promise that resolves with the approved payment token.
   */
  async getApprovedPaymentTokenAsync(props, signedPayload, transactionId) {
    this.logger.debug('Getting approved payment token', props);
    try {
      if (!props) {
        throw new _ProtocolError.ProtocolError('props not provided', {
          component: _sdkTypes.ErrorContextComponent.SDK_GET_APPROVED_PAYMENT_TOKEN,
          description: 'Props required for getting approved payment token were not provided'
        });
      }
      const token = await this.verificationHttpRepo.verifyPaymentAsync({
        signedPayload,
        payload: props,
        transactionId
      });
      this.logger.debug('Approved payment token received');
      return token;
    } catch (error) {
      if (!(error instanceof _ProtocolError.ProtocolError)) this.logger.error("".concat(error.name, ": Unexpected error while trying to get token :: ").concat(error.message), error);
      throw error;
    }
  }

  /**
   * Asynchronously processes a payment by calling the `payPreSendFee` function on a contract.
   * Presend fee is also being calculated based on the token price.
   * @param {PaymentProcessProps} props - The payment information.
   * @return {Promise<void>} - A promise that resolves when the payment has been processed.
   */
  async processPaymentAsync(props) {
    this.logger.debug('Processing payment', props);
    try {
      var _referralDto, _referralDto2;
      const {
        transactionId,
        payload,
        supportedNetwork,
        targetTokenAddress
      } = props;
      let referralDto;
      try {
        // TODO: Remove calls for referral affiliate since it's not used anymore
        referralDto = await this.referralHttpRepo.getReferralAddressForSourceAddressAsync(payload.sourceAddress);
      } catch (error) {
        if (!(error instanceof _ReferralError.ReferralError)) {
          this.logger.error("".concat(error.name, ": Unexpected error while trying to get referral address :: ").concat(error.message), error);
          // throw error;
        }
        this.logger.warn('No affiliate found for source address', payload.sourceAddress);
      }
      const calculationResults = await this.calculatePresendFeeAsync({
        targetTokenAddress,
        targetTokenAmount: payload.targetTokenAmount,
        targetTokenSymbol: payload.targetTokenSymbol,
        nativeTokenSymbol: supportedNetwork.nativeTokenSymbol,
        targetTokenAmountDecimals: payload.targetTokenAmountDecimals,
        sourceAddress: payload.sourceAddress,
        targetAddress: payload.targetAddress,
        supportedNetwork
      });
      this.logger.debug('Presend fee calculation results', calculationResults);
      const contract = new _ethers.ethers.Contract(supportedNetwork.presendPaymentContractAddress, _presendPaymentAbi.presendPaymentAbi, this.signer);
      this.logger.debug("Using payment smart contract address ".concat(supportedNetwork.presendPaymentContractAddress));
      let tx = null;
      if (referralDto && referralDto.address && referralDto.commissionPercentage) {
        tx = await contract['payPreSendFee(address,uint256,uint256,address,uint256)'](targetTokenAddress, calculationResults.tokenAmountInDecimals, calculationResults.tokenPriceNativeWei, referralDto.address, referralDto.commissionPercentage, {
          value: calculationResults.feeInNativeTokenWei
        });
      } else {
        tx = await contract['payPreSendFee(address,uint256,uint256)'](targetTokenAddress, calculationResults.tokenAmountInDecimals, calculationResults.tokenPriceNativeWei, {
          value: calculationResults.feeInNativeTokenWei
          // TODO: Add gas limit if needed
          // gasLimit: calculationResults.estimatedGas
        });
      }
      this.logger.debug('Payment transaction sent', tx.hash);

      // Added unprotected call at the request and full acceptance of risks by Gosu and Drew
      await this.transactionHttpRepo.updateTransactionStatus(transactionId, _sdkTypes.TransactionStatus.Paid);
      const txResult = await tx.wait();
      if (txResult) this.logger.debug('Transaction result', txResult);
      this.logger.debug('Payment processed');
      return {
        affiliateAddress: (_referralDto = referralDto) === null || _referralDto === void 0 ? void 0 : _referralDto.address,
        commissionPercentage: (_referralDto2 = referralDto) === null || _referralDto2 === void 0 ? void 0 : _referralDto2.commissionPercentage,
        tokenPriceUsd: calculationResults.tokenPriceUsd,
        feePaidUsd: calculationResults.feeUsd
      };
    } catch (error) {
      this.logger.error("".concat(error.name, ": Unexpected error while trying to process presend payment :: ").concat(error.message), error);
      throw error;
    }
  }

  /**
   * Calculates the presend fee.
   * @param {CalculateFeeProps} props - The properties required for fee calculation.
   * @returns {Promise<PresendFeeResponse>} - The calculated fee values.
   * @description This method calculates the presend fee based on the provided properties. The formula for fee calculation is as follows:
   *  - fee = max(1 USD, tokenPriceUsd * targetTokenAmount * FEE_PROPORTION)
   *  - fee += 0.0001 USD (to account for aggregator price desync)
   *  - feeInNativeToken = fee / nativeTokenPrice
   */
  async calculatePresendFeeAsync(props) {
    this.logger.debug('Calculating presend fee', props);
    const {
      nativeTokenSymbol,
      targetTokenAddress,
      targetTokenAmount,
      targetTokenSymbol,
      targetTokenAmountDecimals,
      targetAddress,
      sourceAddress,
      supportedNetwork
    } = props;
    const nativeTokenPrice = await this.dexScreenerHttpRepo.getPriceForPairAsync("".concat(nativeTokenSymbol, " USD"));
    this.logger.debug('Native token price in USD', nativeTokenPrice);
    let tokenPriceUsd = nativeTokenPrice;
    if (targetTokenAddress !== _constants.NATIVE_TOKEN_PLACEHOLDER_ADDRESS) {
      try {
        tokenPriceUsd = await this.dexScreenerHttpRepo.getPriceForTokenAddressAsync(targetTokenAddress, targetTokenSymbol);
      } catch (error) {
        if (!(error instanceof _DexScreenerError.DexScreenerError)) {
          this.logger.error("".concat(error.name, ": Unexpected error while trying to find price for token :: ").concat(error.message), error);
          throw error;
        }
        this.logger.warn('No price found for token, keeping token value of 0');
      }
    }
    this.logger.debug('Token price in USD', tokenPriceUsd);

    // fee = 1 USD or proportional fee, whichever is higher
    // proportional fee = tokenPriceUsd * tokenAmount * FEE_PROPORTION (0.2% by default)
    const proportionalFee = tokenPriceUsd * targetTokenAmount * _constants.FEE_PROPORTION;
    let feeUsd = Math.max(_constants.FEE_MIN_USD, proportionalFee);

    // add 0.01 USD to fee to account for aggregator price desync
    feeUsd += 0.02;

    // make sure feeInNativeToken doesn't exceed maximum precision of wei
    const feeInNativeToken = (0, _utils.floatPrecision)(feeUsd / nativeTokenPrice);
    const feeInNativeTokenWei = _ethers.ethers.utils.parseUnits(feeInNativeToken.toString(), 18);
    const tokenPriceNativeWei = _ethers.ethers.utils.parseUnits((0, _utils.floatPrecision)(tokenPriceUsd / nativeTokenPrice).toString(), 18);
    let estimatedGas = _ethers.ethers.BigNumber.from(0);
    try {
      estimatedGas = await this.estimateGasAsync(sourceAddress, targetAddress, feeInNativeTokenWei, supportedNetwork);
      const gas = _ethers.ethers.utils.formatUnits(estimatedGas, 18);
      this.logger.debug('Estimated gas for transaction', gas);
    } catch (error) {
      this.logger.error('Error estimating gas: ', error);
    }
    const tokenAmountInDecimals = _ethers.ethers.utils.parseUnits(targetTokenAmount.toString(), targetTokenAmountDecimals !== null && targetTokenAmountDecimals !== void 0 ? targetTokenAmountDecimals : 18);
    return {
      feeUsd,
      feeInNativeTokenWei,
      tokenPriceNativeWei,
      tokenAmountInDecimals,
      tokenPriceUsd,
      estimatedGas
    };
  }

  /**
   * Get a list of supported exchanges.
   * @return {Promise<string[]>} A promise that resolves with the list of supported exchanges.
   */
  async getSupportedExchangesAsync() {
    try {
      this.logger.debug('Getting supported exchanges');

      // TODO: replicate Redis data instead of merging the two region responses
      const caVerificationHttpRepo = new _VerificationHttpRepository.VerificationHttpRepository(_constants.VerificationURI.CA);
      const usVerificationHttpRepo = new _VerificationHttpRepository.VerificationHttpRepository(_constants.VerificationURI.US);
      const caSupportedExchanges = await caVerificationHttpRepo.getSupportedExchangesAsync();
      const usSupportedExchanges = await usVerificationHttpRepo.getSupportedExchangesAsync();
      const supportedExchanges = [...new Set([...caSupportedExchanges, ...usSupportedExchanges])];
      this.logger.debug('Successfully retrived supported exchanges');
      return supportedExchanges;
    } catch (error) {
      if (!(error instanceof _ProtocolError.ProtocolError)) this.logger.error("".concat(error.name, ": Unexpected Error while trying to retrieve supported exchanges :: ").concat(error.message), error);
      throw error;
    }
  }

  /**
   * Get a list of supported target networks.
   * @return {SupportedNetwork[]} A list of supported target networks.
   */
  async getSupportedNetworksAsync() {
    try {
      this.logger.debug('Getting supported networks');
      const supportedNetworks = await this.verificationHttpRepo.getSupportedNetworksAsync();
      this.logger.debug('Successfully retrieved supported networks');
      return supportedNetworks;
    } catch (error) {
      if (!(error instanceof _ProtocolError.ProtocolError)) this.logger.error("".concat(error.name, ": Unexpected Error while trying to retrieve supported networks :: ").concat(error.message), error);
      throw error;
    }
  }
  async estimateGasAsync(sourceAddress, targetAddress, value, network) {
    this.logger.debug('Estimating gas for transaction', sourceAddress, targetAddress, value);
    try {
      const provider = new _ethers.ethers.providers.JsonRpcProvider(network.rpcUrl);
      const gasEstimate = await provider.estimateGas({
        from: sourceAddress,
        to: targetAddress,
        value
      });
      this.logger.debug("Estimated Gas: ".concat(gasEstimate.toString()));
      return gasEstimate;
    } catch (error) {
      this.logger.error('Error estimating gas: ', error);
      throw error;
    }
  }
  async getBypassEligibilityAsync(sourceAddress, network) {
    this.logger.debug('Checking if source address is eligible for bypass', sourceAddress, network);
    if (!network.presendNftPriContractAddress || !network.presendNftPiiContractAddress) {
      throw new _ProtocolError.ProtocolError('No presend nft contract address found for network', {
        component: _sdkTypes.ErrorContextComponent.SDK_GET_BYPASS_ELIGIBILITY,
        description: 'No presend nft contract address found, this is due to a misconfiguration'
      });
    }
    const bscProvider = new _ethers.ethers.providers.JsonRpcProvider(network.rpcUrl);
    const presendPriNftContract = new _ethers.ethers.Contract(network.presendNftPriContractAddress, _erc721Abi.erc721Abi, bscProvider);
    const presendPiiNftContract = new _ethers.ethers.Contract(network.presendNftPiiContractAddress, _erc721Abi.erc721Abi, bscProvider);
    if (!presendPriNftContract || !presendPiiNftContract) {
      throw new _ProtocolError.ProtocolError('Unable to retrieve Presend NFT contracts', {
        component: _sdkTypes.ErrorContextComponent.SDK_GET_BYPASS_ELIGIBILITY,
        description: 'NFT Contracts were not found on provided network, this is due to a misconfiguration'
      });
    }
    const ownedPriNfts = await presendPriNftContract.balanceOf(sourceAddress);
    const ownedPiiNfts = await presendPiiNftContract.balanceOf(sourceAddress);
    this.logger.debug('Source address owned presend NFTs', {
      ownedPriNfts,
      ownedPiiNfts
    });
    this.logger.debug('OVERRIDE_PAYMENT_BYPASS', _constants.OVERRIDE_PAYMENT_BYPASS);
    return _constants.OVERRIDE_PAYMENT_BYPASS || ownedPriNfts > 0 || ownedPiiNfts > 0;
  }
}
exports.PresendVerificationSDK = PresendVerificationSDK;